import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, languages, languageFlags } from '../locales';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Check localStorage synchronously to avoid flash
  const getSavedLanguage = () => {
    try {
      const saved = localStorage.getItem('preferredLanguage');
      if (saved && languages[saved]) {
        return saved;
      }
    } catch (e) {}
    return null;
  };

  const savedLang = getSavedLanguage();
  const [language, setLanguageState] = useState(savedLang || 'ru');
  const [isReady, setIsReady] = useState(!!savedLang);

  // Get translation string by path (e.g., 'common.loading')
  const t = useCallback((path, params = {}) => {
    const keys = path.split('.');
    let value = translations[language];
    
    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        // Fallback to Russian if not found
        let fallback = translations['ru'];
        for (const k of keys) {
          if (fallback && typeof fallback === 'object') {
            fallback = fallback[k];
          } else {
            return path;
          }
        }
        return fallback || path;
      }
    }
    
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([key, val]) => {
        value = value.replace(new RegExp(`\\{${key}\\}`, 'g'), val);
      });
    }
    
    return value || path;
  }, [language]);

  // Set language and persist
  const setLanguage = useCallback(async (lang, saveToServer = true) => {
    if (!languages[lang]) {
      console.warn(`Unsupported language: ${lang}`);
      return;
    }
    
    setLanguageState(lang);
    try {
      localStorage.setItem('preferredLanguage', lang);
    } catch (e) {}
    
    if (saveToServer) {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axios.put(`${API}/users/me/language?language=${lang}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Failed to save language preference:', error);
        }
      }
    }
  }, []);

  // Auto-detect language by IP on first visit (only if no saved preference)
  useEffect(() => {
    // Already have saved preference - ready immediately, skip network call
    if (getSavedLanguage()) {
      setIsReady(true);
      return;
    }

    // No saved preference - detect by IP but don't block rendering.
    // Use AbortController so the request is cancelled cleanly if the
    // provider unmounts (avoids stray net::ERR_ABORTED entries in console).
    const controller = new AbortController();
    setIsReady(true);

    (async () => {
      try {
        const response = await axios.get(`${API}/detect-language`, {
          timeout: 2000,
          signal: controller.signal,
        });
        const detectedLang = response.data.language;
        if (detectedLang && languages[detectedLang] && detectedLang !== 'ru') {
          setLanguageState(detectedLang);
          localStorage.setItem('preferredLanguage', detectedLang);
        } else {
          localStorage.setItem('preferredLanguage', 'ru');
        }
      } catch (error) {
        // Silently ignore cancellations; persist ru as default on real errors.
        if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          return;
        }
        try {
          localStorage.setItem('preferredLanguage', 'ru');
        } catch (e) {}
      }
    })();

    return () => controller.abort();
  }, []);

  // Load user's language preference on login
  const loadUserLanguage = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/users/me/language`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userLang = response.data.language;
      if (userLang && languages[userLang]) {
        setLanguage(userLang, false);
      }
    } catch (error) {
      console.log('No user language preference found');
    }
  }, [setLanguage]);

  const value = {
    language,
    setLanguage,
    t,
    languages,
    languageFlags,
    isReady,
    loadUserLanguage
  };

  // Show loading only if we don't have a saved preference
  if (!isReady) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
